<template>
  <v-container fluid class="centered-tabs mt-3 mx-1" v-show="showComponent">
    <v-form ref="form">
      <v-row>
        <v-col cols="12">
          <h2 class="primary--text font-weight-bold pt-5 pl-2">
            {{ edit ? $t("book/admin:process-step.edit.titles.edit") : $t("book/admin:process-step.edit.titles.add") }}
          </h2>

          <v-card
              elevation="0"
              flat
              tile
          >
            <v-card-text>
              <v-row justify="space-around">
                <v-col cols="12" md="5">
                  <!-- General Section -->
                  <h4 class="primary--text font-weight-medium">{{ $t('common:general') }}</h4>
                  <!-- Name Input -->
                  <v-text-field
                      outlined
                      clearable
                      v-model="processStep.name"
                      :label="$t('common:name')"
                      :rules="[ruleRequired($t('utils:rules.required'))]"
                      tabindex="1"
                  />

                  <!-- Description Input -->
                  <v-textarea
                      outlined
                      clearable
                      v-model="processStep.description"
                      :label="$t('common:description')"
                      auto-grow
                      tabindex="2"
                  />

                  <!-- Scrollview for Answers -->
                  <v-card outlined class="mb-5" v-if="processStep.answers && processStep.answers.length > 0">
                    <v-virtual-scroll
                        :items="processStep.answers"
                        :item-height="50"
                        height="200"
                    >
                      <template v-slot="{ item }">
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>{{ item }}</v-list-item-title>
                          </v-list-item-content>
                          <v-list-item-action>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on }">
                                <v-icon @click="deleteAnswer(item)" v-on="on">close</v-icon>
                              </template>
                              <span>{{ $t('book/admin:process-step.edit.tooltip-answers') }}</span>
                            </v-tooltip>
                          </v-list-item-action>
                        </v-list-item>
                        <v-divider></v-divider>
                      </template>
                    </v-virtual-scroll>
                  </v-card>
                  <v-card outlined class="mb-5" :class="{ 'error-border': errorOutline } " v-else>
                    <v-card-text>{{ $t('book/admin:process-step.edit.labels.no-answers') }}</v-card-text>
                  </v-card>

                  <!-- Dialog for adding new answers -->
                  <TwoButtonDialog
                      ref="dialogAdd"
                      :add-text-field="true"
                      :add-text-field-label="$t('common:answer')"
                      :btn-text="$t('dialog:add-btn-text', {name : $t('common:answer')})"
                      color="primary"
                      :dialog-title="$t('dialog:add-btn-title', {name : $t('common:answer')})"
                      :button1-text="$t('buttons:add')"
                      :button2-text="$t('buttons:cancel')"
                      @clickedButton1="addAnswer"
                      @clickedButton2="$refs.dialogAdd.showDialog = false"
                  />

                  <!-- Checkbox 'user may add answers' -->
                  <v-checkbox
                      color="secondary"
                      v-model="processStep.userAddsAnswer"
                      :label="$t('book/admin:process-step.edit.selects.check-add-user-msg')"
                      tabindex="3"
                  />

                  <!-- Checkbox 'doc required' -->
                  <v-checkbox
                      color="secondary"
                      v-model="processStep.documentationRequired"
                      :label="$t('book/admin:process-step.edit.selects.check-doc-required')"
                      tabindex="4"
                  />

                  <!-- Message Section -->
                  <h4 class="primary--text font-weight-medium mt-5">{{ $t('book/admin:process-step.edit.titles.msg') }}</h4>
                  <!-- User-Msg Input -->
                  <v-textarea
                      outlined
                      v-model="processStep.userMessage"
                      :label="$t('book/admin:process-step.edit.labels.user-msg')"
                      auto-grow
                      tabindex="5"
                  />

                  <!-- Mail-Msg Input -->
                  <v-textarea
                      outlined
                      v-model="processStep.mailMessage"
                      :label="$t('book/admin:process-step.edit.labels.mail-msg')"
                      auto-grow
                      tabindex="6"
                  />
                </v-col>
                <v-col cols="12" md="5">
                  <!-- Specific Section -->
                  <h4 class="primary--text font-weight-medium">{{ $t('book/admin:process-base.edit.titles.specific') }}</h4>
                  <!-- Checkbox 'can start another process' -->
                  <v-checkbox
                      color="secondary"
                      v-model="startsProcess"
                      :label="$t('book/admin:process-step.edit.selects.check-start-another-process')"
                      tabindex="7"
                  />

                  <!-- Radio Buttons for 'On Failure' and 'On Success' -->
                  <v-radio-group :disabled="!startsProcess"
                                 v-model="radioGroup"
                                 mandatory>
                    <v-radio
                        ref="radioOnFailure"
                        color="secondary"
                        :label="$t('book/admin:process-step.edit.selects.radio-on-failure')"
                        value="1"
                    />
                    <v-radio
                        ref="radioOnSuccess"
                        color="secondary"
                        :label="$t('book/admin:process-step.edit.selects.radio-on-success')"
                        value="0"
                    />
                  </v-radio-group>

                  <!-- Select for selecting following process -->
                  <v-select
                      :disabled="!startsProcess"
                      :items="restProcesses"
                      item-text="name"
                      item-value="id"
                      :menu-props="{ bottom: true, offsetY: true }"
                      v-model="processStep.processToStart"
                      outlined
                      :label="$t('common:process')"
                      clearable
                  />

                  <!-- Other Section -->
                  <h4 class="primary--text font-weight-medium mt-5">{{ $t('book/admin:process-step.edit.titles.other') }}</h4>
                  <!-- Checkbox 'operator' -->
                  <v-checkbox
                      v-model="processStep.contactOperator"
                      color="secondary"
                      :label="$t('book/admin:process-step.edit.selects.check-operator')"
                      tabindex="8"
                  />

                  <!-- Roles Multiselect -->
                  <v-select
                      :items="restRoles"
                      item-text="name"
                      item-value="id"
                      :menu-props="{ bottom: true, offsetY: true }"
                      v-model="processStep.contactRoles"
                      outlined
                      :label="$t('common:role_plural') + '(Opt.)'"
                      clearable
                      multiple
                      chips
                      tabindex="9"
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-card
              elevation="0"
              flat
              tile
              class="pl-3 pt-3 pb-md-3"
          >

            <BaseButton
                color="primary"
                :btn-text="$t('buttons:save')"
                @clickedButton="updateForm()"
            />

            <TwoButtonDialog
                v-if="edit"
                ref="deleteButton"
                :btn-text="$t('buttons:delete')"
                color="error"
                :dialog-title="$t('dialog:delete-btn-title', {name : $t('book/admin:process-step.edit.dialog-part-1')})"
                :dialog-text="$t('dialog:delete-btn-dialog', {name : $t('book/admin:process-step.edit.dialog-part-2')})"
                :button1-text="$t('buttons:cancel')"
                :button2-text="$t('buttons:delete')"
                @clickedButton1="$refs.deleteButton.showDialog = false"
                @clickedButton2="deleteProcessStep()"
            />

            <DirtyConfirmButtonDialog
                ref="dirtyDialog"
                :btn-text="$t('buttons:back')"
                color="grey"
                :dialog-title="$t('dialog:dirty-save-btn-title')"
                :dialog-text="$t('dialog:dirty-save-btn-dialog')"
                :button1-text="$t('dialog:dirty-save-btn-1-text')"
                :button2-text="$t('dialog:dirty-save-btn-2-text')"
                @clickedButton="$router.go(-1)"
                :dataToWatch="processStep"
            />

          </v-card>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import ProcessStep from '@/modules/book/data/processStep';
import TwoButtonDialog from "@/modules/core/components/TwoButtonDialog/TwoButtonDialog";
import ProcessStepService from '@/modules/book/services/process/impl/process-step-service'
import WorkerRoleService from '@/modules/core/services/worker-role-service'
import ProcessService from '@/modules/book/services/process/impl/process-service'
import {ruleRequired} from '@/modules/core/services/rules';
import Utils from "@/modules/core/services/utils";
import DataUtils from "@/modules/core/services/data-utils";
import Process from "@/modules/book/data/process";
import WorkerRole from "@/modules/core/data/worker-role";
import DirtyConfirmButtonDialog from "@/modules/core/components/TwoButtonDialog/DirtyConfirmButtonDialog";
import BaseButton from "@/modules/core/components/Base/BaseButton";

export default {
  name: "ProcessStepForm",
  components: {
    TwoButtonDialog,
    DirtyConfirmButtonDialog,
    BaseButton
  },
  data() {
    return {
      processStep: new ProcessStep(),
      valid: false,
      edit: false,

      restProcesses: [],
      restRoles: [],
      radioGroup: null,
      startsProcess: false,
      errorOutline: false,
      isFetchingProcessSteps: true
    }
  },
  methods: {
    ruleRequired,

    // Process Step API methods
    updateForm() {
      if (!this.$refs.form.validate()) {
        // TODO scroll to specific form field which doesnt require the rules
        window.scrollTo(0, 0);
        return;
      }

      // if there are no answers, make border and text red
      if (this.processStep.answers.length === 0) {
        this.errorOutline = true;
        return;
      }

      this.checkRadios();

      if (!this.edit) {
        ProcessStepService.postToAPI(this.processStep,
          () => {
            Utils.showSuccessSnackbar(this.$t('snackbar:add-success', {name: this.$t('common:process-step')}));
            this.$router.go(-1)
          },
          () => {
            Utils.showFailureSnackbar(this.$t('snackbar:add-failure', {name: this.$t('common:process-step')}))
          }
        );
      } else {
        ProcessStepService.putToAPI(this.processStep,
          () => {
            Utils.showSuccessSnackbar(this.$t('snackbar:edit-success', {name: this.$t('common:process-step')}));
            this.$router.go(-1)
          },
          () => {
            Utils.showFailureSnackbar(this.$t('snackbar:edit-failure', {name: this.$t('common:process-step')}))
          }
        );
      }
    },

    deleteProcessStep() {
      this.$refs.deleteButton.setButtonSettings(true, true, false, true);
      ProcessStepService.deleteFromAPI(this.processStep,
        () => {
          Utils.showSuccessSnackbar(this.$t('snackbar:delete-success', {name: this.$t('common:process-step')}));
          this.$router.go(-1)
        },
        () => {
          this.$refs.deleteButton.setButtonSettings(false, false, false, false);
          this.$refs.deleteButton.showDialog = false;
          Utils.showFailureSnackbar(this.$t('snackbar:delete-failure', {name: this.$t('common:process-step')}))
        }
      )
    },

    // Other methods
    addAnswer() {
      this.processStep.answers.push(this.$refs.dialogAdd.textField);
      this.$refs.dialogAdd.showDialog = false;
      this.$refs.dialogAdd.textField = "";
    },
    deleteAnswer(item) {
      for (var i = 0; i < this.processStep.answers.length; i++) {
        if (item == this.processStep.answers[i]) {
          this.processStep.answers.splice(i, 1);
        }
      }
    },
    checkRadios() {
      if (this.startsProcess) {
        if (this.radioGroup == this.$refs.radioOnFailure.value) {
          this.processStep.startProcessOnFailure = true;
        } else if (this.radioGroup == this.$refs.radioOnSuccess.value) {
          this.processStep.startProcessOnFailure = false;
        }
      } else {
        this.processStep.startProcessOnFailure = null;
        this.processStep.processToStart = null;
      }
    },

    // Different API calls
    getAllWorkerRoles() {
      WorkerRoleService.readAllFromAPI(
        (res) => {
          this.restRoles = DataUtils.parseDataToObjectArray(res.data, WorkerRole);
        },
        (error) => {
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:role_plural')}))
          this.$logger.error("Error at getting Worker Roles: " + error)
        }
      )
    },
    getAllProcesses() {
      ProcessService.readAllFromAPI(
        (res) => {
          this.restProcesses = DataUtils.parseDataToObjectArray(res.data, Process);
        },
        (error) => {
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:process_plural')}))
          this.$logger.error("Error at getting Processes: " + error);
        }
      )
    }
  },
  computed: {
    currentLang() {
      return this.$store.state.translation.language;
    },
    showComponent() {
      if(this.edit) {
        if(this.isFetchingProcessSteps) {
          return false
        } else {
          return true;
        }
      } else {
        return true
      }
    }
  },
  watch: {
    currentLang() {
      Utils.sleep(500).then(() => {
        this.$refs.form.resetValidation()
      });
    }
  },
  created() {
    this.getAllWorkerRoles();
    this.getAllProcesses();

    if (this.$route.params.id) {
      this.edit = true;
      ProcessStepService.readFromAPI(this.$route.params.id,
        (res) => {
          this.processStep = DataUtils.parseDataToSingleObject(res.data, ProcessStep);

          if (this.processStep.processToStart != null) {
            this.startsProcess = true;
            if (this.processStep.startProcessOnFailure) {
              this.radioGroup = "1";
            } else if (!this.processStep.startProcessOnFailure) {
              this.radioGroup = "0";
            }
          }
          this.isFetchingProcessSteps = false;
          this.$refs.dirtyDialog.startWatch();
        },
        (error) => {
          this.$logger.error("Error at getting Process Step: " + error)
          this.$router.push('/404')
        }
      )
    } else {
      this.edit = false;
    }
  },
  mounted() {
    this.$refs.dirtyDialog.startWatch();
  }
}
</script>
<style lang="scss">
@import "src/styles/app";

.error-border {
  border: thin solid $error-color !important;
  transition: border 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);

  &.theme--light.v-card > .v-card__text, .theme--light.v-card .v-card__subtitle {
    color: var(--v-error-base);
  }
}
</style>

